import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { InterceptorSkipHeader } from '@omni/interceptors/token.interceptor';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { LocalizationService } from '@omni/services/localization/localization.service';
import {
  EndpointCatalogue, EndpointCatalogueCache, fetchEndpointCatalogue, recogniserFor, SpeechRecogniser,
  SpeechResource, speechResourceOf,
} from './recogniser-discovery';
import { KeyCheck, keyCheckOf, keyCheckUrl } from './speech-key-check';

/** A discovery request is abandoned after this long, so a dictation is not held waiting on it. */
const DISCOVERY_TIMEOUT_MS = 5000;

/**
 * Chooses the recogniser each dictation runs on, from the rep's language, the
 * tenant's speech configuration and the custom endpoints on the tenant's Speech
 * resource. Every place that starts recognition asks here rather than reading the
 * speech configuration directly.
 */
@Injectable({ providedIn: 'root' })
export class SpeechRecogniserService {
  readonly #auth = inject(AuthenticationService);
  readonly #device = inject(DeviceService);
  readonly #disk = inject(DiskService);
  readonly #http = inject(HttpClient);
  readonly #localization = inject(LocalizationService);

  readonly #catalogue = new EndpointCatalogueCache(
    {
      read: () => this.#disk.retrieve(DB_KEY_PREFIXES.SPEECH_ENDPOINT_CATALOGUE, true),
      write: async (catalogue: EndpointCatalogue) => {
        await this.#disk.updateOrInsert(DB_KEY_PREFIXES.SPEECH_ENDPOINT_CATALOGUE, () => ({ ...catalogue }));
      },
    },
    (resource) => fetchEndpointCatalogue(resource, (url) => this.#read(url, resource)),
    () => !this.#device.isOffline,
  );

  /**
   * The recogniser for the language the rep has selected in the app, chosen afresh
   * on every call so a change of language applies to the next dictation.
   *
   * @returns `null` when no speech service is configured, or when no recogniser
   *   serves the rep's language. Callers that have already checked the configuration
   *   treat `null` as the language being unavailable.
   */
  public async choose(): Promise<SpeechRecogniser | null> {
    const config = this.#auth.speechConfig;
    const resource = speechResourceOf(config);
    if (!resource) {
      return null;
    }
    const catalogue = await this.#catalogue.get(resource);
    return recogniserFor(config, this.#localization.selectedLanguage?.localeID, catalogue);
  }

  /**
   * Reports that recognition on a recogniser was cancelled with an error. When it
   * ran on a custom endpoint the catalogue is fetched afresh before the next choice:
   * a browser reports a refused endpoint and a dropped connection alike, as a
   * WebSocket closed with code 1006, so every such cancellation counts.
   */
  public reportCancelled(recogniser: SpeechRecogniser | null | undefined): void {
    if (recogniser?.endpointId) {
      this.#catalogue.invalidate();
    }
  }

  /**
   * Asks the service directly whether it accepts the recogniser's key in its
   * region, for a connection that failed before it ever opened. The key is sent
   * only in the request header, and neither it nor the response is logged.
   */
  public async checkKey(recogniser: SpeechRecogniser): Promise<KeyCheck> {
    const headers = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': recogniser.subscriptionKey,
      [InterceptorSkipHeader]: 'true',
    });
    try {
      await firstValueFrom(this.#http.post(keyCheckUrl(recogniser.region), null, { headers, responseType: 'text' })
        .pipe(timeout(DISCOVERY_TIMEOUT_MS)));
      return 'accepted';
    } catch (error) {
      return keyCheckOf(error instanceof HttpErrorResponse ? error.status : null);
    }
  }

  /** GET on the Speech REST API with the resource key and without the app's bearer token. */
  #read(url: string, resource: SpeechResource): Promise<unknown> {
    const headers = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': resource.subscriptionKey,
      [InterceptorSkipHeader]: 'true',
    });
    return firstValueFrom(this.#http.get(url, { headers }).pipe(timeout(DISCOVERY_TIMEOUT_MS)));
  }
}
